import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import { useState, useCallback, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BiX, BiArrowBack } from "react-icons/bi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import AuthContext from "../../../context/AuthProvider";
import "../css/RoomModal.css";

// Same flow as the mobile app (add_studentbyadmin_page.dart):
//   1. Enter the parent's email -> GET admission-form/check-parent
//   2a. found       -> parent already has an account: send them an admission form link
//   2b. not_found   -> create the parent + student(s) in one go (parents/create-with-student)
//   2c. invalid_type-> email belongs to a non-parent account
const STEPS = {
  EMAIL: "email",
  FOUND: "found",
  NOT_FOUND: "not_found",
  FORM: "form",
};

const EMAIL_REGEX = /\S+@\S+\.\S+/;

const emptyChild = () => ({
  firstName: "",
  lastName: "",
  birthDate: "",
  gender: "",
  roomIds: [],
});

const emptyParent = { firstName: "", lastName: "", phone: "", gender: "" };

// Flattens DRF error bodies like { parent: { email: ["..."] } } into one message
const extractError = (data) => {
  if (!data) return null;
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return extractError(data[0]);
  if (data.detail) return data.detail;
  if (data.message) return data.message;
  const firstKey = Object.keys(data)[0];
  return firstKey ? extractError(data[firstKey]) : null;
};

export default function AddStudentModal({ open, onClose, onCreated }) {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { school, auth } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const L = (en, ar) => (isAr ? ar : en);

  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [invalidType, setInvalidType] = useState(false);
  const [checking, setChecking] = useState(false);

  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [selectedLinkId, setSelectedLinkId] = useState("");
  const [sending, setSending] = useState(false);

  const [parent, setParent] = useState(emptyParent);
  const [children, setChildren] = useState([emptyChild()]);
  const [genders, setGenders] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resetAll = useCallback(() => {
    setStep(STEPS.EMAIL);
    setEmail("");
    setEmailError("");
    setInvalidType(false);
    setLinks([]);
    setSelectedLinkId("");
    setParent(emptyParent);
    setChildren([emptyChild()]);
    setErrors({});
    setServerError("");
  }, []);

  const handleClose = () => {
    resetAll();
    onClose();
  };

  // Genders + rooms are only needed by the create form
  useEffect(() => {
    if (!open || step !== STEPS.FORM || (genders.length && rooms.length)) return;

    const loadOptions = async () => {
      try {
        const [gendersRes, roomsRes] = await Promise.all([
          axiosPrivate.get(API_URLS.gender),
          axiosPrivate.get(`${API_URLS.getRooms(auth.id, school.id)}?page_size=100`),
        ]);
        setGenders(gendersRes.data.map((g) => ({ id: g.value, name: g.label })));
        const roomRows = Array.isArray(roomsRes.data)
          ? roomsRes.data
          : roomsRes.data?.results || [];
        setRooms(roomRows.map((room) => ({ id: room.id, name: room.name })));
      } catch (err) {
        console.error("Failed to load student form options", err);
      }
    };

    loadOptions();
  }, [open, step, genders.length, rooms.length, axiosPrivate, auth.id, school.id]);

  const loadAdmissionLinks = async () => {
    setLinksLoading(true);
    try {
      const response = await axiosPrivate.get(API_URLS.getAdmissionLinks(school.id));
      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setLinks(rows.filter((link) => link.is_usable ?? (link.is_active && !link.is_expired)));
    } catch (err) {
      console.error("Failed to load admission links", err);
      setLinks([]);
    } finally {
      setLinksLoading(false);
    }
  };

  const handleCheckEmail = async () => {
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError(L("Enter a valid email address", "أدخل بريدًا إلكترونيًا صحيحًا"));
      return;
    }

    setChecking(true);
    setEmailError("");
    setInvalidType(false);
    try {
      const response = await axiosPrivate.get(API_URLS.checkParentEmail(trimmed));
      const status = String(response.data?.status || "").toLowerCase();

      if (status === "invalid_type") {
        setInvalidType(true);
      } else if (status === "found") {
        setStep(STEPS.FOUND);
        await loadAdmissionLinks();
      } else if (status === "not_found") {
        setStep(STEPS.NOT_FOUND);
      } else {
        setEmailError(t("form.genericError"));
      }
    } catch (err) {
      setEmailError(extractError(err.response?.data) || t("form.genericError"));
    } finally {
      setChecking(false);
    }
  };

  const handleSendAdmissionLink = async () => {
    if (!selectedLinkId) return;
    setSending(true);
    try {
      const response = await axiosPrivate.post(API_URLS.sendAdmissionFormEmail(school.id), {
        email: email.trim(),
        admission_link_id: selectedLinkId,
      });
      toast.success(
        response.data?.message ||
          L("Admission form link sent to the parent", "تم إرسال رابط نموذج التقديم لولي الأمر")
      );
      handleClose();
    } catch (err) {
      toast.error(extractError(err.response?.data) || t("form.genericError"));
    } finally {
      setSending(false);
    }
  };

  const updateParent = (field) => (e) => {
    setParent((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [`parent.${field}`]: undefined }));
  };

  const updateChild = (index, field, value) => {
    setChildren((prev) =>
      prev.map((child, i) => (i === index ? { ...child, [field]: value } : child))
    );
    setErrors((prev) => ({ ...prev, [`child.${index}.${field}`]: undefined }));
  };

  const validateForm = () => {
    const required = L("This field is required", "هذا الحقل مطلوب");
    const newErrors = {};
    if (!parent.firstName.trim()) newErrors["parent.firstName"] = required;
    if (!parent.lastName.trim()) newErrors["parent.lastName"] = required;

    children.forEach((child, i) => {
      if (!child.firstName.trim()) newErrors[`child.${i}.firstName`] = required;
      if (!child.lastName.trim()) newErrors[`child.${i}.lastName`] = required;
      if (child.roomIds.length === 0) {
        newErrors[`child.${i}.roomIds`] = L(
          "Please select at least one room",
          "يرجى اختيار فصل واحد على الأقل"
        );
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Only send optional fields when they have a value — empty strings fail backend validation
  const buildStudentPayload = (child) => {
    const payload = {
      first_name: child.firstName.trim(),
      last_name: child.lastName.trim(),
    };
    if (child.birthDate) payload.birth_date = child.birthDate;
    if (child.gender) payload.gender = child.gender;
    return payload;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setServerError("");
    try {
      const parentPayload = {
        first_name: parent.firstName.trim(),
        last_name: parent.lastName.trim(),
        email: email.trim(),
      };
      if (parent.phone.trim()) parentPayload.phone = parent.phone.trim();
      if (parent.gender) parentPayload.gender = parent.gender;

      const [firstChild, ...otherChildren] = children;
      const response = await axiosPrivate.post(API_URLS.createParentWithStudent(school.id), {
        parent: parentPayload,
        student: buildStudentPayload(firstChild),
        room_ids: firstChild.roomIds,
      });

      const parentId = response.data?.parent?.id;
      const firstStudentId = response.data?.student?.id;

      // Any extra children are created separately and linked to the new parent
      let extraChildError = null;
      for (const child of otherChildren) {
        try {
          await axiosPrivate.post(API_URLS.addStudents(school.id), {
            ...buildStudentPayload(child),
            parents: [parentId],
            room_ids: child.roomIds,
          });
        } catch (err) {
          extraChildError = extractError(err.response?.data) || t("form.genericError");
          break;
        }
      }

      if (extraChildError) {
        toast.error(extraChildError);
      } else {
        toast.success(L("Parent and student created successfully", "تم إنشاء ولي الأمر والطالب بنجاح"));
      }

      onCreated?.();
      handleClose();
      if (firstStudentId) navigate(`details/${firstStudentId}`);
    } catch (err) {
      console.error("Create parent with student failed", err);
      setServerError(extractError(err.response?.data) || t("form.genericError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === STEPS.FORM) {
      setStep(STEPS.NOT_FOUND);
    } else {
      setStep(STEPS.EMAIL);
      setSelectedLinkId("");
    }
    setServerError("");
  };

  const renderLabel = (text, required) => (
    <Typography variant="body1" component="label" display="block" color="secondary" gutterBottom>
      {text} {required && <span className="text-danger">*</span>}
    </Typography>
  );

  const renderInput = ({ label, value, onChange, error, type = "text", required, placeholder, disabled }) => (
    <Box flex={1} mb={3}>
      {renderLabel(label, required)}
      <input
        type={type}
        value={value}
        onChange={onChange}
        className={`form-control ${error ? "is-invalid" : ""}`}
        placeholder={placeholder ?? label}
        disabled={disabled || submitting}
      />
      {error && <Typography className="errorText">{error}</Typography>}
    </Box>
  );

  const renderSelect = ({ label, value, onChange, options, multiple, error, required }) => (
    <Box flex={1} mb={3}>
      {renderLabel(label, required)}
      <Select
        fullWidth
        displayEmpty
        multiple={multiple}
        value={value}
        onChange={onChange}
        error={Boolean(error)}
        disabled={submitting}
        renderValue={(selected) => {
          const ids = multiple ? selected : selected ? [selected] : [];
          if (ids.length === 0) return `--- ${t("actions.select")} ---`;
          return options
            .filter((opt) => ids.includes(opt.id))
            .map((opt) => t(`options.${opt.name}`, { defaultValue: opt.name }))
            .join(", ");
        }}
        sx={{
          height: "40px",
          "& .MuiSelect-select": { paddingTop: "8px", paddingBottom: "8px" },
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            {t(`options.${opt.name}`, { defaultValue: opt.name })}
          </MenuItem>
        ))}
      </Select>
      {error && <Typography className="errorText">{error}</Typography>}
    </Box>
  );

  const emailChip = (
    <Box
      mb={3}
      px={2}
      py={1}
      sx={{ background: "#f5eef9", borderRadius: "50px", display: "inline-block" }}
    >
      <Typography variant="body2" color="secondary">
        {email.trim()}
      </Typography>
    </Box>
  );

  const renderEmailStep = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {L(
          "Start with the parent's email. We'll check if the parent already has an account.",
          "ابدأ ببريد ولي الأمر الإلكتروني. سنتحقق مما إذا كان لديه حساب بالفعل."
        )}
      </Typography>
      <Box mb={3}>
        {renderLabel(L("Parent Email", "البريد الإلكتروني لولي الأمر"), true)}
        <input
          type="email"
          value={email}
          className={`form-control ${emailError ? "is-invalid" : ""}`}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError("");
            setInvalidType(false);
          }}
          onKeyDown={(e) => e.key === "Enter" && !checking && handleCheckEmail()}
          placeholder="parent@example.com"
          disabled={checking}
          autoFocus
        />
        {emailError && <Typography className="errorText">{emailError}</Typography>}
      </Box>

      {invalidType && (
        <Box mb={3} p={1.5} sx={{ background: "rgba(255, 87, 34, 0.08)", borderRadius: "8px" }}>
          <Typography variant="body2" sx={{ color: "#e64a19" }}>
            {L(
              "This email belongs to an account that is not a parent. Please use a different email.",
              "هذا البريد مرتبط بحساب ليس ولي أمر. يرجى استخدام بريد آخر."
            )}
          </Typography>
        </Box>
      )}

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={handleCheckEmail}
          disabled={checking}
          startIcon={checking ? <CircularProgress size={20} color="inherit" /> : null}
          className="submitButton"
        >
          {L("Check Email", "تحقق من البريد")}
        </Button>
      </Box>
    </>
  );

  const renderFoundStep = () => (
    <>
      <Typography variant="h6" color="secondary" mb={1}>
        {L("Parent found", "تم العثور على ولي الأمر")}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {L(
          "This parent already has an account. Send them an admission form link to enroll their child.",
          "ولي الأمر لديه حساب بالفعل. أرسل له رابط نموذج التقديم لتسجيل طفله."
        )}
      </Typography>
      {emailChip}

      {linksLoading ? (
        <Box display="flex" justifyContent="center" mb={3}>
          <CircularProgress size={28} />
        </Box>
      ) : links.length === 0 ? (
        <Box mb={3} p={2} sx={{ background: "#f5f5f5", borderRadius: "8px", textAlign: "center" }}>
          <Typography variant="body2">
            {L(
              "There are no active admission form links. Create one from the Admission Form page first.",
              "لا توجد روابط تقديم نشطة. أنشئ رابطًا من صفحة نموذج التقديم أولاً."
            )}
          </Typography>
        </Box>
      ) : (
        renderSelect({
          label: L("Choose admission form link", "اختر رابط نموذج التقديم"),
          value: selectedLinkId,
          onChange: (e) => setSelectedLinkId(e.target.value),
          options: links.map((link) => ({
            id: link.id,
            name: link.title || L("Admission Form", "نموذج التقديم"),
          })),
          required: true,
        })
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button onClick={handleBack} sx={{ textTransform: "none", color: "#702E8F" }}>
          {L("Check another email", "تحقق من بريد آخر")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendAdmissionLink}
          disabled={!selectedLinkId || sending || linksLoading}
          startIcon={sending ? <CircularProgress size={20} color="inherit" /> : null}
          className="submitButton"
        >
          {L("Send Admission Link", "إرسال رابط التقديم")}
        </Button>
      </Box>
    </>
  );

  const renderNotFoundStep = () => (
    <>
      <Typography variant="h6" color="secondary" mb={1}>
        {L("Parent not found", "لم يتم العثور على ولي الأمر")}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {L(
          "No account uses this email. Create the parent and the student together.",
          "لا يوجد حساب بهذا البريد. أنشئ ولي الأمر والطالب معًا."
        )}
      </Typography>
      {emailChip}

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button onClick={handleBack} sx={{ textTransform: "none", color: "#702E8F" }}>
          {L("Check another email", "تحقق من بريد آخر")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setStep(STEPS.FORM)}
          className="submitButton"
        >
          {L("Create Student with Parent", "إنشاء طالب مع ولي الأمر")}
        </Button>
      </Box>
    </>
  );

  const renderFormStep = () => (
    <>
      <Typography variant="h6" color="secondary" mb={2}>
        {L("Parent Information", "بيانات ولي الأمر")}
      </Typography>
      <Box display="flex" gap={2}>
        {renderInput({
          label: L("First Name", "الاسم الأول"),
          value: parent.firstName,
          onChange: updateParent("firstName"),
          error: errors["parent.firstName"],
          required: true,
        })}
        {renderInput({
          label: L("Last Name", "الاسم الأخير"),
          value: parent.lastName,
          onChange: updateParent("lastName"),
          error: errors["parent.lastName"],
          required: true,
        })}
      </Box>
      <Box display="flex" gap={2}>
        {renderInput({
          label: L("Email", "البريد الإلكتروني"),
          value: email.trim(),
          onChange: () => {},
          disabled: true,
        })}
        {renderInput({
          label: L("Phone Number", "رقم الهاتف"),
          value: parent.phone,
          onChange: updateParent("phone"),
          type: "tel",
          placeholder: "+201234567890",
        })}
      </Box>
      <Box display="flex" gap={2}>
        {renderSelect({
          label: L("Gender", "النوع"),
          value: parent.gender,
          onChange: updateParent("gender"),
          options: genders,
        })}
        <Box flex={1} />
      </Box>

      {children.map((child, index) => (
        <Box key={index}>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="secondary">
              {L("Student Information", "بيانات الطالب")}
              {children.length > 1 ? ` (${index + 1})` : ""}
            </Typography>
            {children.length > 1 && (
              <Button
                color="error"
                size="small"
                onClick={() => setChildren((prev) => prev.filter((_, i) => i !== index))}
                disabled={submitting}
                sx={{ textTransform: "none" }}
              >
                {L("Remove", "إزالة")}
              </Button>
            )}
          </Box>
          <Box display="flex" gap={2}>
            {renderInput({
              label: L("First Name", "الاسم الأول"),
              value: child.firstName,
              onChange: (e) => updateChild(index, "firstName", e.target.value),
              error: errors[`child.${index}.firstName`],
              required: true,
            })}
            {renderInput({
              label: L("Last Name", "الاسم الأخير"),
              value: child.lastName,
              onChange: (e) => updateChild(index, "lastName", e.target.value),
              error: errors[`child.${index}.lastName`],
              required: true,
            })}
          </Box>
          <Box display="flex" gap={2}>
            {renderInput({
              label: L("Birth Date", "تاريخ الميلاد"),
              value: child.birthDate,
              onChange: (e) => updateChild(index, "birthDate", e.target.value),
              type: "date",
            })}
            {renderSelect({
              label: L("Gender", "النوع"),
              value: child.gender,
              onChange: (e) => updateChild(index, "gender", e.target.value),
              options: genders,
            })}
          </Box>
          {renderSelect({
            label: L("Rooms", "الفصول"),
            value: child.roomIds,
            onChange: (e) => updateChild(index, "roomIds", e.target.value),
            options: rooms,
            multiple: true,
            error: errors[`child.${index}.roomIds`],
            required: true,
          })}
        </Box>
      ))}

      <Button
        onClick={() => setChildren((prev) => [...prev, emptyChild()])}
        disabled={submitting}
        sx={{ color: "#702E8F", fontWeight: 600, textTransform: "none", mb: 2 }}
      >
        + {L("Add another child", "إضافة طفل آخر")}
      </Button>

      {serverError && (
        <Typography className="errorText" mb={2}>
          {serverError}
        </Typography>
      )}

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
          className="submitButton"
        >
          {t("modal.addTitle", { title: t("admin_titles.student") })}
        </Button>
      </Box>
    </>
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="modalStyle">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center" gap={1}>
            {step !== STEPS.EMAIL && (
              <IconButton onClick={handleBack} size="small" disabled={submitting || sending}>
                <BiArrowBack style={{ transform: isAr ? "rotate(180deg)" : "" }} />
              </IconButton>
            )}
            <Typography variant="h5" fontWeight="bold" color="secondary">
              {t("modal.titleInfo", { title: t("admin_titles.student") })}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <BiX />
          </IconButton>
        </Box>

        {step === STEPS.EMAIL && renderEmailStep()}
        {step === STEPS.FOUND && renderFoundStep()}
        {step === STEPS.NOT_FOUND && renderNotFoundStep()}
        {step === STEPS.FORM && renderFormStep()}
      </Box>
    </Modal>
  );
}
